import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon, PlayCircleIcon, ClockIcon, TagIcon } from 'lucide-react';
interface TutorialCardProps {
  id: number;
  title: string;
  description: string;
  category: string;
  duration: string;
  completed: boolean;
  completedAt: string | null;
  icon: React.ReactNode;
}
export function TutorialCard({
  id,
  title,
  description,
  category,
  duration,
  completed,
  completedAt,
  icon
}: TutorialCardProps) {
  const navigate = useNavigate();
  // Format the completion date if available
  const formatCompletionDate = (dateString: string | null) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  };
  const completionDate = formatCompletionDate(completedAt);
  const handleCardClick = () => {
    navigate(`/tutorial/${id}`);
  };
  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click when button is clicked
    navigate(`/tutorial/${id}`);
  };
  return <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm flex flex-col h-full cursor-pointer" onClick={handleCardClick} data-id="element-4749">
      <div className="p-5 flex-grow" data-id="element-4750">
        <div className="flex justify-between items-start mb-3" data-id="element-4751">
          <div className="p-2 rounded-lg bg-gray-50" data-id="element-4752">{icon}</div>
          <div className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${completed ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-gray-100 text-gray-600 border border-gray-200'}`} data-id="element-4753">
            {completed ? 'Completed' : 'Not completed'}
          </div>
        </div>
        <h3 className="text-sm font-semibold text-gray-900 mb-2" data-id="element-4754">{title}</h3>
        <p className="text-sm text-gray-500 mb-4 line-clamp-2" data-id="element-4755">{description}</p>
        <div className="flex items-center justify-between mt-auto" data-id="element-4756">
          <div className="flex items-center text-xs text-gray-500" data-id="element-4757">
            <TagIcon size={14} className="mr-1" data-id="element-4758" />
            {category}
          </div>
          <div className="flex items-center text-xs text-gray-500" data-id="element-4759">
            <ClockIcon size={14} className="mr-1" data-id="element-4760" />
            {duration}
          </div>
        </div>
      </div>
      <div className={`px-5 py-3 border-t border-gray-100 flex justify-between items-center ${completed ? 'bg-green-50/50' : 'bg-gray-50/50'}`} data-id="element-4761">
        <button onClick={handleButtonClick} className={`flex items-center text-sm font-medium transition-colors ${completed ? 'text-gray-600 hover:text-gray-700' : 'text-blue-600 hover:text-blue-700'}`} data-id="element-4762">
          {completed ? <>
              <CheckCircleIcon size={16} className="mr-1.5" data-id="element-4763" />
              Revisit Tutorial
            </> : <>
              <PlayCircleIcon size={16} className="mr-1.5" data-id="element-4764" />
              Start Tutorial
            </>}
        </button>
        {completed && completionDate && <span className="text-xs text-gray-500" data-id="element-4765">
            Completed {completionDate}
          </span>}
      </div>
    </div>;
}